/**
 * @file Calls to a shop action endpoint, which answers `{success, data}` or `{success: false, error, ...details}`.
 */
import { csrfFetchJson } from './csrf-fetch.js';
import { lockControl } from './lock-control.js';

/**
 * @param {string} url
 * @param {URLSearchParams|FormData} body
 * @returns {Promise<any>} the `data` of a successful response; a failure throws with the answer as `response`
 */
export async function postAction(url, body) {
  const result = await csrfFetchJson(url, { method: 'POST', body, headers: { Accept: 'application/json' } });
  if (!result.success) {
    const failure = new Error(result.error ?? 'not done');
    failure.response = result;
    throw failure;
  }

  return result.data;
}

/**
 * Runs an action with its trigger disabled, so a double click cannot post it twice.
 *
 * @param {HTMLButtonElement} trigger
 * @param {() => Promise<void>} action
 * @param {(error: Error) => void} onError receives the endpoint's error code as `message`
 */
export async function runWithTriggerDisabled(trigger, action, onError) {
  const unlock = lockControl(trigger);
  try {
    await action();
  } catch (error) {
    onError(error);
  } finally {
    unlock();
  }
}
